import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, finalize } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environment/environment';
import { User } from '../models/userModel';
import { Group, ClientRole, AddUserPayload, AddUserResponse } from '../models/userManagement';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class KeycloakAuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  private baseUrl = environment.serverBaseUrl;

  constructor(private router: Router) {
    if (isPlatformBrowser(this.platformId)) {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        this.currentUserSubject.next(JSON.parse(storedUser));
      } else {
        this.captureSessionIdFromUrl();
        this.fetchUser().catch(() => {
          // Handle errors in fetchUser, e.g., redirect to login on failure
          this.router.navigate(['/login'], {
            queryParams: { message: 'Please log in to continue.' },
          });
        });
      }
    }
  }

  getAccessToken(): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    return localStorage.getItem('session_id') || '';
  }

  private captureSessionIdFromUrl(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      localStorage.setItem('session_id', sessionId);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  isLoggedIn(): boolean {
    if (this.currentUserSubject.value) return true;

    if (isPlatformBrowser(this.platformId)) {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        this.currentUserSubject.next(JSON.parse(storedUser));
        return true;
      }
    }
    return false;
  }

  currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  login(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    window.location.href = `${this.baseUrl}/auth/login`;
  }

  logout(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.http.get(`${this.baseUrl}/auth/logout`, { headers: this.getAuthHeaders() })
      .pipe(
        finalize(() => {
          this.clearSession();
          this.router.navigate(['/login'], {
            queryParams: { message: 'You have been logged out.' },
          });
        })
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          console.error('Logout API error:', err);
          // Clear session even if logout API fails
          this.clearSession();
          this.router.navigate(['/login'], {
            queryParams: { message: 'Session ended. Please log in again.' },
          });
        }
      });
  }

  loginDigifyPassword(email: string, password: string): Promise<{ message: string; sessionId: string; redirectTo: string }> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve({ message: '', sessionId: '', redirectTo: '' });
    return new Promise((resolve, reject) => {
      this.http
        .post<{ message: string; sessionId: string; redirectTo: string }>(`${this.baseUrl}/login-password`, { email, password })
        .subscribe({
          next: (response) => {
            console.log('Login Password API response:', response);
            resolve(response);
          },
          error: (err) => {
            console.error('Login Password API error:', err);
            reject(err);
          },
        });
    });
  }

  forgotPassword(email: string): Promise<{ message: string; resetToken: string }> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve({ message: '', resetToken: '' });
    return new Promise((resolve, reject) => {
      this.http
        .post<{ message: string; resetToken: string }>(`${this.baseUrl}/forgot-password`, { email })
        .subscribe({
          next: (response) => {
            console.log('Forgot Password API response:', response);
            resolve(response);
          },
          error: (err: HttpErrorResponse) => {
            console.error('Forgot Password API error:', err);
            reject(err);  // Pass full HttpErrorResponse
          },
        });
    });
  }

  resetPassword(resetToken: string, newPassword: string): Promise<{ message: string; redirectTo: string }> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve({ message: '', redirectTo: '' });
    return new Promise((resolve, reject) => {
      this.http
        .post<{ message: string; redirectTo: string }>(`${this.baseUrl}/reset-password`, { resetToken, newPassword })
        .subscribe({
          next: (response) => {
            console.log('Reset Password API response:', response);
            resolve(response);
          },
          error: (err: HttpErrorResponse) => {
            console.error('Reset Password API error:', err);
            const errorMessage = err.status === 404 ? 'Invalid or expired reset token' : 'Failed to reset password';
            reject({ error: errorMessage });
          },
        });
    });
  }

  /** GET /show-group  →  list of groups in the realm */
  getGroups(): Observable<Group[]> {
    return this.http.get<Group[]>(`${this.baseUrl}/show-group`)
  }
  
  /** GET /show-client-roles  →  list of client-roles for my-angular-client */
  getClientRoles(): Observable<ClientRole[]> {
    return this.http.get<ClientRole[]>(`${this.baseUrl}/show-client-roles`)
  }

  /** POST /add-user  →  create a new user (with permanent password) */
  addUser(payload: AddUserPayload): Observable<AddUserResponse> {
    return this.http.post<AddUserResponse>(`${this.baseUrl}/add-user`, payload, {
      headers: this.getAuthHeaders(),
    });
  }
   
  private clearSession(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('session_id');
      localStorage.removeItem('currentUser');
    }
    this.currentUserSubject.next(null);
  }

  fetchUser(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return Promise.resolve();
    return new Promise((resolve, reject) => {
      this.http.get<{ user: any }>(`${this.baseUrl}/api/me`, { headers: this.getAuthHeaders() })
        .subscribe({
          next: response => {
            const profile = response.user;
            const roles_reversed = profile.roles.reverse();
            const mappedUser: User = {
              id: profile.sub || '',
              username: profile.preferred_username || '',
              firstName: profile.given_name || '',
              lastName: profile.family_name || '',
              email: profile.email || '',
              picture: '',
              groups: profile.groups || [],
              roles: roles_reversed || []
            };
            this.currentUserSubject.next(mappedUser);
            localStorage.setItem('currentUser', JSON.stringify(mappedUser));
            resolve();
          },
          error: (err: HttpErrorResponse) => {
            console.error('Fetch user error:', err);
            if (err.status === 401 && err.error?.error === 'Token expired') {
              this.clearSession();
              this.router.navigate(['/login'], {
                queryParams: { message: 'Your session has expired. Please log in again.' },
              });
            }
            this.currentUserSubject.next(null);
            reject(err);
          }
        });
    });
  }

  public hydrateUserFromStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.currentUserSubject.value) {
      const storedUser = localStorage.getItem('currentUser');
      if (storedUser) {
        this.currentUserSubject.next(JSON.parse(storedUser));
      }
    }
  }

  private getAuthHeaders(): HttpHeaders {
    const token = this.getAccessToken();
    return new HttpHeaders({ 'Authorization': token ? `Bearer ${token}` : '' });
  }
}